import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';

export type SubscriptionStatus = 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'EXPIRED';

export interface Promotion {
  promoId: string;
  name: string;
  type: 'FREE_PERIOD' | string;
  durationMonths?: number | null;
  appliesTo: 'ANY_PLAN' | string;
  eligibilityStrategy: 'SIGNUP_BEFORE_DATE' | 'FIRST_N_RESTAURANTS' | 'MANUAL_GRANT' | string;
  stackable: boolean;
  status: 'ACTIVE' | 'ARCHIVED' | string;
  createdAt: number;
  updatedAt?: number | null;
}

export interface RestaurantSubscription {
  restaurantId: string;
  subscriptionId: string;
  planId: string;
  priceId: string;
  status: SubscriptionStatus;
  activePromotionId?: string | null;
  activePromotionName?: string | null;
  activePromotion?: Promotion | null;
  promoEndsAt?: number | null;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  addOns: { addonId: string; quantity: number; status: string }[];
}

export interface SubscriptionPlan {
  planId: string;
  name: string;
  description?: string | null;
  currentPrice?: { amountCents: number; currency: string; billingInterval: string } | null;
}

export interface PaymentMethodSummary {
  restaurantId: string;
  hasPaymentMethod: boolean;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
}

export interface SubscriptionInvoice {
  invoiceId: string;
  amountDueCents: number;
  amountPaidCents: number;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE' | string;
  periodStart: number;
  periodEnd: number;
  issuedAt: number;
  dueAt: number;
  paidAt?: number | null;
}

export interface InvoiceList {
  restaurantId: string;
  invoices: SubscriptionInvoice[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  httpClient = inject(HttpClient);

  constructor() {}

  getSubscriptionPlans(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/subscriptions/plans').pipe(
      catchError((error) => {
        console.error('Error fetching stations:', error);
        return of(error);
      }),
    );
  }

  getSubscriptionAddons(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/subscriptions/addons').pipe(
      catchError((error) => {
        console.error('Error fetching stations:', error);
        return of(error);
      }),
    );
  }

  getMySubscription(): Observable<any> {
    return this.httpClient.get<any>(`${ServiceConstant.apiUrl}/subscriptions/me`).pipe(
      catchError((error) => {
        console.error('Error fetching subscription:', error);
        return of(error);
      }),
    );
  }

  getPlan(planId: string): Observable<any> {
    return this.httpClient
      .get<any>(`${ServiceConstant.apiUrl}/subscriptions/plans/${encodeURIComponent(planId)}`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching plan:', error);
          return of(error);
        }),
      );
  }

  getMyPaymentMethod(): Observable<any> {
    return this.httpClient
      .get<any>(`${ServiceConstant.apiUrl}/subscriptions/me/payment-method`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching payment method:', error);
          return of(error);
        }),
      );
  }

  getMyInvoices(limit = 100): Observable<any> {
    return this.httpClient
      .get<any>(`${ServiceConstant.apiUrl}/subscriptions/me/invoices`, { params: { limit } })
      .pipe(
        catchError((error) => {
          console.error('Error fetching invoices:', error);
          return of(error);
        }),
      );
  }

  /** Preview of the upcoming invoice at the current plan/add-on state — nothing is charged. */
  getMyNextInvoice(): Observable<any> {
    return this.httpClient.get<any>(`${ServiceConstant.apiUrl}/subscriptions/me/next-invoice`).pipe(
      catchError((error) => {
        console.error('Error fetching next invoice preview:', error);
        return of(error);
      }),
    );
  }

  /** This period's billable orders and estimated per-order charge so far (FNF-1284). */
  getMyUsage(): Observable<any> {
    return this.httpClient.get<any>(`${ServiceConstant.apiUrl}/subscriptions/me/usage`).pipe(
      catchError((error) => {
        console.error('Error fetching usage:', error);
        return of(error);
      }),
    );
  }

  /** Stripe-hosted Checkout (setup mode) to add or replace the card on file. Returns `{ url }`. */
  createPaymentMethodCheckoutSession(returnUrl: string): Observable<any> {
    return this.httpClient
      .post<any>(`${ServiceConstant.apiUrl}/subscriptions/me/payment-method/checkout-session`, {
        returnUrl,
      })
      .pipe(
        catchError((error) => {
          console.error('Error starting Stripe Checkout:', error);
          return of(error);
        }),
      );
  }

  /** Stripe Customer Portal: manage card, view and download invoices. Returns `{ url }`. */
  createBillingPortalSession(returnUrl: string): Observable<any> {
    return this.httpClient
      .post<any>(`${ServiceConstant.apiUrl}/subscriptions/me/billing-portal-session`, {
        returnUrl,
      })
      .pipe(
        catchError((error) => {
          console.error('Error opening Stripe billing portal:', error);
          return of(error);
        }),
      );
  }

  /** Self-service: restaurantId is derived from the JWT on the gateway, not passed here. */
  includeAddonsWithSubscription(payload: any): Observable<any> {
    return this.httpClient
      .post<any>(`${ServiceConstant.apiUrl}/subscriptions/me/addons`, payload)
      .pipe(
        catchError((error) => {
          console.error('Error adding addons:', error);
          return of(error);
        }),
      );
  }

  updateSubscriptionAddonQuantity(addonId: string, payload: { quantity: number }): Observable<any> {
    return this.httpClient
      .patch<any>(`${ServiceConstant.apiUrl}/subscriptions/me/addons/${addonId}`, payload)
      .pipe(
        catchError((error) => {
          console.error('Error updating addon quantity:', error);
          return of(error);
        }),
      );
  }

  removeSubscriptionAddon(addonId: string): Observable<any> {
    return this.httpClient
      .delete<any>(`${ServiceConstant.apiUrl}/subscriptions/me/addons/${addonId}`)
      .pipe(
        catchError((error) => {
          console.error('Error removing addon:', error);
          return of(error);
        }),
      );
  }

  /** Cancel a downgrade scheduled for period end; the current plan carries on (FNF-1297). */
  cancelPendingPlanChange(): Observable<any> {
    return this.httpClient
      .delete<any>(`${ServiceConstant.apiUrl}/subscriptions/me/plan/pending`)
      .pipe(
        catchError((error) => {
          console.error('Error canceling scheduled plan change:', error);
          return of(error);
        }),
      );
  }

  updateSubscriptionPlan(payload: any): Observable<any> {
    return this.httpClient
      .patch<any>(`${ServiceConstant.apiUrl}/subscriptions/me/plan`, payload)
      .pipe(
        catchError((error) => {
          console.error('Error updating plan:', error);
          return of(error);
        }),
      );
  }
}
